export const TRACK_TIMELINE_NORMALIZATION_EPSILON_SECONDS = 0.001

interface TimelineRegion {
  duration: number
  id: string
  position: number
  trackId: string
}

interface StreamingTimelineOverlapDetails {
  nextRegionId: string
  nextRegionStart: number
  overlapSeconds: number
  previousRegionEnd: number
  previousRegionId: string
  toleranceSeconds: number
  trackId: string
}

export class StreamingTimelineOverlapError extends Error {
  nextRegionId: string
  nextRegionStart: number
  overlapSeconds: number
  previousRegionEnd: number
  previousRegionId: string
  toleranceSeconds: number
  trackId: string

  constructor(details: StreamingTimelineOverlapDetails) {
    super(
      `Streaming timeline overlap on track ${details.trackId} between ${details.previousRegionId} and ${details.nextRegionId} exceeds ${details.toleranceSeconds}s (${details.overlapSeconds}s)`
    )
    Object.setPrototypeOf(this, StreamingTimelineOverlapError.prototype)
    this.name = "StreamingTimelineOverlapError"
    this.nextRegionId = details.nextRegionId
    this.nextRegionStart = details.nextRegionStart
    this.overlapSeconds = details.overlapSeconds
    this.previousRegionEnd = details.previousRegionEnd
    this.previousRegionId = details.previousRegionId
    this.toleranceSeconds = details.toleranceSeconds
    this.trackId = details.trackId
  }
}

interface NormalizeTrackTimelineOptions {
  epsilonSeconds?: number
  onOverlap?: (error: StreamingTimelineOverlapError) => void
}

/**
 * Normalizes each track timeline into exact non-overlapping adjacency for the
 * streaming runtime, while surfacing larger same-track overlaps via `onOverlap`.
 */
export function normalizeTrackTimeline<T extends TimelineRegion>(
  regions: T[],
  options: NormalizeTrackTimelineOptions = {}
): T[] {
  const epsilonSeconds =
    options.epsilonSeconds ?? TRACK_TIMELINE_NORMALIZATION_EPSILON_SECONDS
  const normalizedRegions = regions.map((region) => ({ ...region }))
  const normalizedRegionsByTrack = new Map<string, T[]>()

  for (const region of normalizedRegions) {
    const trackRegions = normalizedRegionsByTrack.get(region.trackId) ?? []
    trackRegions.push(region)
    normalizedRegionsByTrack.set(region.trackId, trackRegions)
  }

  for (const trackRegions of normalizedRegionsByTrack.values()) {
    trackRegions.sort((left, right) => left.position - right.position)

    for (let index = 1; index < trackRegions.length; index++) {
      const previousRegion = trackRegions[index - 1]
      const nextRegion = trackRegions[index]
      const previousRegionEnd = previousRegion.position + previousRegion.duration
      const deltaSeconds = nextRegion.position - previousRegionEnd

      if (deltaSeconds < -epsilonSeconds) {
        options.onOverlap?.(new StreamingTimelineOverlapError({
          nextRegionId: nextRegion.id,
          nextRegionStart: nextRegion.position,
          overlapSeconds: Math.abs(deltaSeconds),
          previousRegionEnd,
          previousRegionId: previousRegion.id,
          toleranceSeconds: epsilonSeconds,
          trackId: previousRegion.trackId,
        }))
      }

      if (deltaSeconds < 0) {
        previousRegion.duration = Math.max(0, nextRegion.position - previousRegion.position)
        continue
      }

      if (deltaSeconds > 0 && deltaSeconds <= epsilonSeconds) {
        nextRegion.position = previousRegionEnd
      }
    }
  }

  return normalizedRegions
}
